// GuildSetupTour.tsx
// ---------------------------------------------------------------------------
// The owner-side twin of OnboardingTour: a non-locking spotlight walkthrough
// shown once, right after someone creates their first guild. A brand new guild
// is an empty room, and the things that actually fill it are, in order:
//
//   1. Share the join link      2. Add the Discord bot
//   3. Release your raiders     4. Find the Dashboard
//   5. Onboard from Discord
//
// It opens on a "guild created" card listing that route, which then fades out
// over the spotlight's own dim, so the first amber ring arrives with context
// instead of landing cold on someone who just hit Save.
//
// Same visual language as the character tour (amber ring, dark tooltip, pill)
// and the same rules: the dim is painted with a huge box-shadow on a
// pointer-events:none element, so the page stays fully clickable and nobody is
// ever trapped mid-tour.
//
// What differs from OnboardingTour
// --------------------------------
// * It navigates. The character tour lives entirely on the Roster and only
//   waits for the player to arrive; this one is a guided route through the
//   guild page, so each step actively asks the host to open the pane it needs
//   (Guild Info for steps 1-2, Dashboard > Requests for step 3).
// * It is scoped to one guild. The tour remembers which guild it was queued
//   for, so opening a different guild collapses it to the pill rather than
//   spotlighting the wrong page.
// * No celebration. The character tour earns its "Ready to Raid" finale;
//   finishing this one just gets out of the way.
//
// Public API
// ----------
//   requestGuildSetupTour(guildId, guildName?)  -> call after a guild is created
//   startGuildSetupTour(guildId)                -> force-replay
//   hasCompletedGuildSetupTour()                -> bool
//   resetGuildSetupTour()                       -> clear the flag (debugging)
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";

export const GUILD_SETUP_TOUR_KEY = "lfm_guild_setup_tour_v1";
const GUILD_SETUP_GUILD_KEY = "lfm_guild_setup_tour_guild";
const GUILD_SETUP_NAME_KEY = "lfm_guild_setup_tour_name";
const TOUR_EVENT = "lfm:guild-setup-tour";

/** Which pane of the guild detail page a step lives on. */
export type GuildStage = "info" | "dashboard";

type FlagValue = "pending" | "done" | null;

function readFlag(): FlagValue {
  try {
    return (localStorage.getItem(GUILD_SETUP_TOUR_KEY) as FlagValue) ?? null;
  } catch {
    return null;
  }
}
function writeFlag(v: Exclude<FlagValue, null>) {
  try {
    localStorage.setItem(GUILD_SETUP_TOUR_KEY, v);
  } catch {
    /* ignore (private mode etc.) */
  }
}
function readGuildName(): string {
  try {
    return localStorage.getItem(GUILD_SETUP_NAME_KEY) || "";
  } catch {
    return "";
  }
}
function readTourGuild(): string {
  try {
    return localStorage.getItem(GUILD_SETUP_GUILD_KEY) || "";
  } catch {
    return "";
  }
}
function writeTourGuild(guildId: string, guildName?: string) {
  try {
    localStorage.setItem(GUILD_SETUP_GUILD_KEY, guildId);
    if (guildName) localStorage.setItem(GUILD_SETUP_NAME_KEY, guildName);
  } catch {
    /* ignore */
  }
}

/** Has the owner already finished / dismissed the guild tour? */
export function hasCompletedGuildSetupTour(): boolean {
  return readFlag() === "done";
}

/**
 * Queue the tour for a just-created guild. No-op if the owner has already seen
 * it. Engages as soon as they are looking at that guild's page, and otherwise
 * waits as a pill.
 */
export function requestGuildSetupTour(guildId: string, guildName?: string) {
  if (readFlag() === "done") return;
  writeFlag("pending");
  writeTourGuild(guildId, guildName);
  window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "request", guildId } }));
}

/** Force the tour from step 1, ignoring the one-time flag. */
export function startGuildSetupTour(guildId?: string, guildName?: string) {
  if (guildId) writeTourGuild(guildId, guildName);
  window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "start", guildId } }));
}

/** Clear the one-time flag (QA / "show me again"). */
export function resetGuildSetupTour() {
  try {
    localStorage.removeItem(GUILD_SETUP_TOUR_KEY);
    localStorage.removeItem(GUILD_SETUP_GUILD_KEY);
  } catch {
    /* ignore */
  }
}

// --- Steps ------------------------------------------------------------------

type Placement = "top" | "bottom";

interface Step {
  key: string;
  stage: GuildStage;
  selector: string;
  title: string;
  body: string;
  placement: Placement;
  radius: number;
  icon: React.ReactNode;
  /** How long to keep hunting for the target before giving up, in ms. */
  waitMs?: number;
  /** Earns an "important" badge: easy to skip, expensive to have missed. */
  important?: boolean;
}

const ICON = {
  link: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
  ),
  discord: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.5m7.5 4.5v-4.5M4.5 8.25c2.5-1.5 5-2.25 7.5-2.25s5 .75 7.5 2.25c1.5 3 2.25 6 1.5 9-1.5 1.125-3 1.875-4.5 2.25M4.5 8.25c-1.5 3-2.25 6-1.5 9 1.5 1.125 3 1.875 4.5 2.25M9 13.5h.008v.008H9V13.5zm6 0h.008v.008H15V13.5z" />
  ),
  release: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
  ),
  dashboard: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  ),
  radar: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  ),
};

const STEPS: Step[] = [
  {
    key: "invite",
    stage: "info",
    selector: '[data-tour="guild-invite"]',
    title: "Share your join link",
    body:
      "This is how players get in. Send the link (or just the code) to your raiders. They pick a character, land in your roster, and their availability starts flowing into your schedule right away.",
    placement: "bottom",
    radius: 14,
    icon: ICON.link,
  },
  {
    key: "discord",
    stage: "info",
    selector: '[data-tour="guild-discord"]',
    title: "Add the bot to Discord",
    body:
      "Your guild already lives in Discord, so put Rallify there too. The bot posts weekly availability checks, takes signups and lets people apply with /ralli join, no chasing required.",
    placement: "bottom",
    radius: 14,
    icon: ICON.discord,
  },
  {
    key: "release",
    stage: "info",
    selector: '[data-tour="guild-release"]',
    title: "Release your raiders each week",
    body:
      "This is the one to remember. Flip a week on and any player who opted into cross-guild raiding is lent to other guilds' raids for that week, so your bench still gets to play instead of sitting out. It covers one week only, so it is a weekly habit. The same switch sits on the home page next to your guild, and under Publish when you build a raid.",
    placement: "bottom",
    radius: 10,
    icon: ICON.release,
    important: true,
  },
  {
    key: "panes",
    stage: "info",
    selector: '[data-tour="guild-panes"]',
    title: "This is your dashboard switch",
    body:
      "Guild Info is the public face of the guild. Dashboard is where you run it: roster, raids, attendance, loot and applications. Remember this toggle, it is how you get back. Hit Next and we will open it for you.",
    placement: "bottom",
    radius: 12,
    icon: ICON.dashboard,
  },
  {
    key: "recruit",
    stage: "dashboard",
    selector: '[data-tour="guild-recruit"]',
    title: "Onboard from Discord",
    body:
      "Once the bot is in, scan a Discord role (your raider role works well) and we show you exactly who is already on Rallify and who still needs to sign up. One post nudges the stragglers.",
    placement: "top",
    radius: 16,
    icon: ICON.radar,
    // The dashboard mounts and fetches on arrival, so give this target longer.
    waitMs: 12000,
  },
];

// --- Geometry helpers -------------------------------------------------------

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOT_PAD = 8;
const TIP_W = 372;
const GAP = 16;

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function rectsEqual(a: Rect | null, b: Rect | null) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

interface GuildSetupTourProps {
  /** The guild whose detail page is open, or null in the directory list. */
  openGuildId?: string | null;
  /** Which pane of that detail page is showing, or null when not in detail. */
  stage?: GuildStage | null;
  /** Ask the host to open a guild's detail page (used by the pill). */
  onOpenGuild?: (guildId: string) => void;
  /** Ask the host to switch panes. Called whenever a step needs the other one. */
  onRequestStage?: (stage: GuildStage) => void;
}

export default function GuildSetupTour({
  openGuildId = null,
  stage = null,
  onOpenGuild,
  onRequestStage,
}: GuildSetupTourProps) {
  const [phase, setPhase] = useState<"idle" | "active">("idle");
  const [minimized, setMinimized] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [tipH, setTipH] = useState(190);
  const [moveAnim, setMoveAnim] = useState(false);
  const [tourGuildId, setTourGuildId] = useState<string>(() => readTourGuild());
  // The "guild created" card that opens the tour. It shares the spotlight's
  // exact dim colour and fades out over it, so the ring is revealed rather
  // than dropped on someone who has no idea what they're looking at.
  const [showIntro, setShowIntro] = useState(false);
  const [introFading, setIntroFading] = useState(false);
  const [guildName, setGuildName] = useState<string>(() => readGuildName());
  // Replays (the guild page's help button) reuse the intro, minus the "you just
  // created this" framing.
  const [isReplay, setIsReplay] = useState(false);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const onTourGuild = !!tourGuildId && openGuildId === tourGuildId;

  const openGuildRef = useRef(openGuildId);
  openGuildRef.current = openGuildId;
  const stageRef = useRef(stage);
  stageRef.current = stage;
  const scrolledForStep = useRef<number>(-1);
  const lastRectRef = useRef<Rect | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  // --- Lifecycle -----------------------------------------------------------

  const begin = useCallback((gid?: string, replay = false) => {
    const target = gid || readTourGuild();
    if (gid) writeTourGuild(gid);
    setTourGuildId(target);
    setGuildName(readGuildName());
    setIsReplay(replay);
    setShowIntro(true);
    setIntroFading(false);
    setStepIndex(0);
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setPhase("active");
    // Created from the guild page itself, so we are almost always already
    // there. If not, wait politely as the pill.
    setMinimized(openGuildRef.current !== target);
  }, []);

  // `returnHome` is for exits from the tooltip itself: the last step lives deep
  // in the dashboard, and leaving someone stranded there is a poor ending. Pill
  // dismissals pass nothing, so an officer already working in the dashboard is
  // never yanked back to Guild Info.
  const finish = useCallback(
    (returnHome = false) => {
      writeFlag("done");
      setPhase("idle");
      setRect(null);
      if (returnHome && stageRef.current !== "info") onRequestStage?.("info");
    },
    [onRequestStage]
  );

  const goNext = useCallback(() => {
    if (stepIndex >= STEPS.length - 1) {
      finish(true);
      return;
    }
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setStepIndex(stepIndex + 1);
  }, [stepIndex, finish]);

  const goBack = useCallback(() => {
    if (stepIndex <= 0) return;
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setStepIndex(stepIndex - 1);
  }, [stepIndex]);

  const goToStep = useCallback((idx: number) => {
    const i = Math.max(0, Math.min(STEPS.length - 1, idx));
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setStepIndex(i);
    setMinimized(false);
    setPhase("active");
  }, []);

  useEffect(() => {
    try {
      (window as any).__lfmGuildTourMounted = true;
    } catch {
      /* ignore */
    }
    return () => {
      try {
        (window as any).__lfmGuildTourMounted = false;
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    setMoveAnim(true);
    const t = window.setTimeout(() => setMoveAnim(false), 380);
    return () => window.clearTimeout(t);
  }, [stepIndex]);

  // --- Triggers ------------------------------------------------------------

  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.action === "start") begin(detail.guildId, true);
      else if (detail.action === "request" && readFlag() !== "done") begin(detail.guildId, false);
      else if (detail.action === "goto") goToStep(detail.index ?? 0);
    };
    window.addEventListener(TOUR_EVENT, onEvt);
    return () => window.removeEventListener(TOUR_EVENT, onEvt);
  }, [begin, goToStep]);

  // A tour queued before a reload (or before this page mounted) resumes as the
  // pill, never by yanking the owner somewhere they didn't ask to go.
  useEffect(() => {
    if (readFlag() === "pending" && readTourGuild()) {
      setTourGuildId(readTourGuild());
      setGuildName(readGuildName());
      setShowIntro(true);
      setStepIndex(0);
      setPhase("active");
      setMinimized(openGuildRef.current !== readTourGuild());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Navigation: drive the host to the pane this step needs --------------

  useEffect(() => {
    if (phase !== "active" || minimized || !onTourGuild) return;
    if (stage !== step.stage) onRequestStage?.(step.stage);
  }, [phase, minimized, onTourGuild, stage, step.stage, onRequestStage]);

  // Leaving the guild collapses the tour; coming back re-engages it.
  useEffect(() => {
    if (phase !== "active") return;
    if (onTourGuild) {
      if (minimized) {
        scrolledForStep.current = -1;
        lastRectRef.current = null;
        setMinimized(false);
      }
    } else if (!minimized) {
      setMinimized(true);
      lastRectRef.current = null;
      setRect(null);
    }
  }, [onTourGuild, phase, minimized]);

  // --- Target finding + live position tracking -----------------------------

  const engaged = phase === "active" && !minimized && onTourGuild && stage === step.stage;
  // Declared here (not down with the intro's own effects) so the target hunt
  // below can see it without reading a stale closure.
  const introVisible = showIntro && phase === "active" && !minimized && onTourGuild && stepIndex === 0;

  useEffect(() => {
    if (!engaged) return;
    let cancelled = false;
    let findTimer = 0;
    let attempts = 0;
    const maxAttempts = Math.ceil((step.waitMs ?? 4000) / 100);

    const measure = (el: HTMLElement): Rect => {
      const r = el.getBoundingClientRect();
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    };

    const commit = (next: Rect) => {
      if (cancelled) return;
      if (rectsEqual(lastRectRef.current, next)) return;
      lastRectRef.current = next;
      setRect(next);
    };

    const locate = (): HTMLElement | null =>
      document.querySelector(step.selector) as HTMLElement | null;

    const tryEngage = () => {
      const el = locate();
      if (!el) return false;
      if (scrolledForStep.current !== stepIndex) {
        scrolledForStep.current = stepIndex;
        const r = el.getBoundingClientRect();
        const tall = r.height > window.innerHeight * 0.6;
        el.scrollIntoView({ behavior: "smooth", block: tall ? "start" : "center" });
      }
      commit(measure(el));
      return true;
    };

    if (!tryEngage()) {
      findTimer = window.setInterval(() => {
        attempts += 1;
        if (tryEngage()) {
          window.clearInterval(findTimer);
        } else if (attempts >= maxAttempts) {
          if (cancelled) return;
          // The intro card is still up and nobody is watching this step yet,
          // so keep hunting instead of burning it.
          if (introVisible) {
            attempts = 0;
            return;
          }
          window.clearInterval(findTimer);
          // Target never showed (the Discord panel needs the bot URL, the
          // dashboard needs its fetch). Move on rather than sitting on a dead
          // step; on the last one that just means a quiet exit.
          if (isLast) finish(true);
          else goNext();
        }
      }, 100);
    }

    let raf = 0;
    const reposition = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = locate();
        if (el && !cancelled) commit(measure(el));
      });
    };
    const tracker = window.setInterval(reposition, 250);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      cancelled = true;
      window.clearInterval(findTimer);
      window.clearInterval(tracker);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [engaged, introVisible, stepIndex, step.selector, step.waitMs, isLast, goNext, finish]);

  useLayoutEffect(() => {
    if (engaged && rect && tipRef.current) {
      const h = tipRef.current.offsetHeight;
      if (h && Math.abs(h - tipH) > 4) setTipH(h);
    }
  }, [engaged, rect, stepIndex, tipH]);

  // --- Intro card ----------------------------------------------------------

  const dismissIntro = useCallback(() => {
    setIntroFading(true);
    window.setTimeout(() => {
      setShowIntro(false);
      setIntroFading(false);
    }, 320);
  }, []);

  // Long enough to read the guild name and the route ahead, short enough that
  // nobody feels held up. Any click or key moves it along early.
  useEffect(() => {
    if (!introVisible || introFading) return;
    const t = window.setTimeout(dismissIntro, 5200);
    return () => window.clearTimeout(t);
  }, [introVisible, introFading, dismissIntro]);

  // Esc dismisses the intro, then collapses to the pill rather than nuking the
  // tour outright.
  useEffect(() => {
    if (!engaged && !introVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (introVisible) dismissIntro();
      else setMinimized(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [engaged, introVisible, dismissIntro]);

  // Same global handshake the character tour uses, so the knight helper and the
  // Raid Ready flash-spotlight stand down while this runs.
  useEffect(() => {
    const active = phase !== "idle";
    try {
      (window as any).__lfmTourActive = active;
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent("lfm:tour-active", { detail: { active } }));
    return () => {
      try {
        (window as any).__lfmTourActive = false;
      } catch {
        /* ignore */
      }
    };
  }, [phase]);

  if (phase !== "active") return null;

  // --- Render: intro ("guild created", fading into step 1) -----------------

  if (introVisible) {
    return (
      <div
        className="fixed inset-0 z-[9991] flex items-center justify-center p-4"
        style={{ pointerEvents: "none" }}
        aria-live="polite"
      >
        <style>{`
          @keyframes guildIntroDim { 0% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes guildIntroCardIn { 0% { opacity:0; transform: translateY(14px) scale(0.94); } 100% { opacity:1; transform: translateY(0) scale(1); } }
          @keyframes guildIntroCardOut { 0% { opacity:1; transform: scale(1); } 100% { opacity:0; transform: scale(1.03); } }
          @keyframes guildIntroCrest { 0% { opacity:0; transform: scale(0.5) rotate(-8deg); } 60% { opacity:1; transform: scale(1.08) rotate(2deg); } 100% { transform: scale(1) rotate(0deg); } }
          @keyframes guildIntroRow { 0% { opacity:0; transform: translateX(-8px); } 100% { opacity:1; transform: translateX(0); } }
        `}</style>

        {/* Same dim the spotlight paints, so the handover reads as one shot. */}
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(2,4,9,0.74)",
            animation: introFading ? undefined : "guildIntroDim 0.45s ease-out both",
          }}
        />

        <div
          role="dialog"
          aria-label="Guild created"
          onClick={dismissIntro}
          style={{
            pointerEvents: "auto",
            animation: introFading
              ? "guildIntroCardOut 0.32s ease-in both"
              : "guildIntroCardIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275) both",
          }}
          className="relative w-full max-w-[440px] cursor-pointer rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[var(--c-surface)] to-[var(--c-surface)] p-7 text-center shadow-2xl shadow-black/70"
        >
          <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

          <div
            className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-900/40"
            style={{ animation: "guildIntroCrest 0.6s cubic-bezier(0.175,0.885,0.32,1.275) 0.1s both" }}
          >
            <svg className="h-8 w-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
            </svg>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700/80 dark:text-amber-300/80">
            {isReplay ? "Guild setup" : "Guild created"}
          </div>
          <h2 className="mt-1.5 text-[26px] font-black leading-tight text-[var(--c-ink)]">
            {guildName || (isReplay ? "Setting up your guild" : "Your guild is live")}
          </h2>
          <p className="mx-auto mt-2 max-w-[340px] text-[14px] leading-relaxed text-[var(--c-ink-2)]">
            {isReplay
              ? "Here is the run through that gets players in, and we will point at each piece as we go."
              : "It is empty for about thirty more seconds. Here is the run through that fills it, and we will point at each piece as we go."}
          </p>

          {/* The route ahead, so the first ring has context before it lands. */}
          <div className="mt-5 space-y-1.5 text-left">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className="flex items-center gap-3 rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] px-3 py-2"
                style={{ animation: `guildIntroRow 0.4s ease-out ${0.28 + i * 0.09}s both` }}
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-amber-500/15 text-[11px] font-black text-amber-700 dark:text-amber-300 ring-1 ring-amber-400/30">
                  {i + 1}
                </span>
                <svg className="h-4 w-4 shrink-0 text-amber-700/70 dark:text-amber-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  {s.icon}
                </svg>
                <span className="text-[13.5px] font-semibold text-[var(--c-ink-2)]">{s.title}</span>
                {s.important && (
                  <span className="ml-auto shrink-0 rounded bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-amber-800 dark:text-amber-200">
                    Important
                  </span>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissIntro();
            }}
            className="mt-6 w-full rounded-xl bg-[var(--c-accent)] px-6 py-2.5 text-[13px] font-bold uppercase tracking-wide text-[var(--c-ink-on-fill)] shadow-lg shadow-amber-900/30 transition-colors hover:bg-[var(--c-accent-hover)]"
          >
            Show me around
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              finish();
            }}
            className="mt-2 w-full py-1.5 text-[12px] font-medium text-[var(--c-ink-4)] transition-colors hover:text-[var(--c-ink-2)]"
          >
            I'll figure it out myself
          </button>
        </div>
      </div>
    );
  }

  // --- Render: pill (waiting / collapsed) ----------------------------------

  const showSpotlight = engaged && rect;

  if (!showSpotlight) {
    return (
      <button
        type="button"
        onClick={() => {
          if (!onTourGuild && tourGuildId) onOpenGuild?.(tourGuildId);
          else {
            scrolledForStep.current = -1;
            setMinimized(false);
          }
        }}
        className="group fixed bottom-5 left-5 z-[9992] flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-[var(--c-surface)] px-4 py-3 shadow-2xl shadow-black/60 backdrop-blur-md transition-all hover:border-amber-400/70 hover:bg-[var(--c-sunken-2)]"
        style={{ animation: "guildTourPillIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275) both" }}
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-amber-400/40">
          <svg className="h-5 w-5 text-amber-700 dark:text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {step.icon}
          </svg>
          <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
          </span>
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700/80 dark:text-amber-300/80">
            Guild setup · Step {stepIndex + 1} of {STEPS.length}
          </span>
          <span className="text-[15px] font-bold text-[var(--c-ink)]">Finish setting up your guild</span>
        </span>
        <svg className="h-4 w-4 text-amber-700/60 dark:text-amber-300/60 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        <span
          role="button"
          tabIndex={0}
          aria-label="Dismiss guild setup guide"
          onClick={(e) => {
            e.stopPropagation();
            finish();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              finish();
            }
          }}
          className="ml-1 grid h-6 w-6 place-items-center rounded-lg text-[var(--c-ink-4)] transition-colors hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink-2)]"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
        <style>{`@keyframes guildTourPillIn{0%{opacity:0;transform:translateY(16px) scale(0.92)}100%{opacity:1;transform:translateY(0) scale(1)}}`}</style>
      </button>
    );
  }

  // --- Render: spotlight + tooltip -----------------------------------------

  const r = rect!;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spotTop = r.top - SPOT_PAD;
  const spotLeft = r.left - SPOT_PAD;
  const spotW = r.width + SPOT_PAD * 2;
  const spotH = r.height + SPOT_PAD * 2;

  const spaceAbove = spotTop;
  const spaceBelow = vh - (spotTop + spotH);
  let placeBelow = step.placement === "bottom";
  if (placeBelow && spaceBelow < tipH + GAP + 12) placeBelow = false;
  if (!placeBelow && spaceAbove < tipH + GAP + 12) placeBelow = spaceBelow > spaceAbove;

  const tipTop = placeBelow
    ? clamp(spotTop + spotH + GAP, 12, vh - tipH - 12)
    : clamp(spotTop - GAP - tipH, 12, vh - tipH - 12);

  const targetCenterX = r.left + r.width / 2;
  const tipLeft = clamp(targetCenterX - TIP_W / 2, 12, vw - TIP_W - 12);
  const arrowLeft = clamp(targetCenterX - tipLeft, 22, TIP_W - 22);

  return (
    <div className="fixed inset-0 z-[9990]" style={{ pointerEvents: "none" }} aria-live="polite">
      <style>{`
        @keyframes guildTourSpotPulse {
          0%,100% { box-shadow: 0 0 0 9999px rgba(2,4,9,0.74), 0 0 0 2px rgba(245,158,11,1), 0 0 0 7px rgba(245,158,11,0.22), 0 0 30px 8px rgba(245,158,11,0.38); }
          50%     { box-shadow: 0 0 0 9999px rgba(2,4,9,0.74), 0 0 0 3px rgba(245,158,11,1), 0 0 0 12px rgba(245,158,11,0.12), 0 0 42px 13px rgba(245,158,11,0.52); }
        }
        @keyframes guildTourTipIn { 0% { opacity:0; transform: translateY(10px) scale(0.96);} 100% { opacity:1; transform: translateY(0) scale(1);} }
      `}</style>

      {/* Spotlight — pointer-events:none so the whole page stays clickable. */}
      <div
        style={{
          position: "fixed",
          top: spotTop,
          left: spotLeft,
          width: spotW,
          height: spotH,
          borderRadius: step.radius,
          pointerEvents: "none",
          transition: moveAnim
            ? "top 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1), width 0.35s cubic-bezier(0.4,0,0.2,1), height 0.35s cubic-bezier(0.4,0,0.2,1)"
            : "none",
          animation: "guildTourSpotPulse 2.4s ease-in-out infinite",
        }}
      />

      {/* Tooltip — the only part that captures pointer events. */}
      <div
        ref={tipRef}
        role="dialog"
        aria-label={step.title}
        style={{
          position: "fixed",
          top: tipTop,
          left: tipLeft,
          width: TIP_W,
          pointerEvents: "auto",
          animation: "guildTourTipIn 0.28s cubic-bezier(0.175,0.885,0.32,1.275) both",
        }}
        className="rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[var(--c-surface)] to-[var(--c-surface)] shadow-2xl shadow-black/70"
      >
        <div
          style={{
            position: "absolute",
            left: arrowLeft - 8,
            ...(placeBelow ? { top: -8 } : { bottom: -8 }),
            width: 16,
            height: 16,
            transform: "rotate(45deg)",
            background: placeBelow ? "#1a1610" : "#0f0c08",
            borderTop: placeBelow ? "1px solid rgba(245,158,11,0.45)" : "none",
            borderLeft: placeBelow ? "1px solid rgba(245,158,11,0.45)" : "none",
            borderBottom: placeBelow ? "none" : "1px solid rgba(245,158,11,0.45)",
            borderRight: placeBelow ? "none" : "1px solid rgba(245,158,11,0.45)",
          }}
        />

        <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

        <div className="p-5">
          <div className="mb-3 flex items-start gap-3.5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-400/25 to-amber-600/10 ring-1 ring-amber-400/50 shadow-inner">
              <svg className="h-[22px] w-[22px] text-amber-700 dark:text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                {step.icon}
              </svg>
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700/90 dark:text-amber-300/90">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
                </span>
                Guild setup · Step {stepIndex + 1} of {STEPS.length}
                {step.important && (
                  <span className="rounded bg-amber-400/20 px-1.5 py-0.5 text-[9px] tracking-[0.14em] text-amber-800 dark:text-amber-200">
                    Important
                  </span>
                )}
              </div>
              <h3 className="mt-1 text-[18px] font-black leading-tight text-[var(--c-ink)]">{step.title}</h3>
            </div>
            <button
              type="button"
              onClick={() => setMinimized(true)}
              aria-label="Minimize guide"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--c-ink-3)] transition-colors hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          <p className="text-[14.5px] leading-relaxed text-[var(--c-ink-2)]">{step.body}</p>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <span
                  key={s.key}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === stepIndex ? "w-6 bg-amber-400" : i < stepIndex ? "w-2 bg-amber-400/60" : "w-2 bg-[var(--c-sunken-2)]"
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="rounded-lg px-3 py-2 text-[13px] font-semibold text-[var(--c-ink-3)] transition-colors hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={goNext}
                className="flex items-center gap-1.5 rounded-xl bg-[var(--c-accent)] px-5 py-2 text-[13px] font-bold uppercase tracking-wide text-[var(--c-ink-on-fill)] shadow-lg shadow-amber-900/30 transition-colors hover:bg-[var(--c-accent-hover)]"
              >
                {isLast ? "Finish" : "Next"}
                {!isLast && (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => finish(true)}
          className="w-full rounded-b-2xl border-t border-[var(--c-line-soft)] py-2.5 text-[12px] font-medium text-[var(--c-ink-3)] transition-colors hover:bg-[var(--c-bg-veil)] hover:text-[var(--c-ink-2)]"
        >
          Skip the tour
        </button>
      </div>
    </div>
  );
}

// Debug controls, mirroring the onboardingTour convention.
if (typeof window !== "undefined") {
  const warnIfUnmounted = () => {
    if (!(window as any).__lfmGuildTourMounted) {
      console.warn(
        "[GuildSetupTour] Component isn't mounted. Open the Guilds tab (it lives on the guild page)."
      );
      return false;
    }
    return true;
  };

  (window as any).guildSetupTour = {
    /** Force the tour from step 1. Pass a guild id, or reuse the stored one. */
    start: (guildId?: string) => {
      warnIfUnmounted();
      startGuildSetupTour(guildId);
    },
    /** Jump to a step: 0=join link, 1=Discord bot, 2=release, 3=dashboard switch, 4=recruit. */
    goto: (index: number = 0) => {
      warnIfUnmounted();
      window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "goto", index } }));
    },
    /** Simulate the real trigger (respects the one-time flag + pill flow). */
    request: (guildId: string) => {
      warnIfUnmounted();
      requestGuildSetupTour(guildId);
    },
    /** Clear the one-time flag so creating a guild shows it again. */
    reset: () => {
      resetGuildSetupTour();
      console.info("[GuildSetupTour] Flag cleared. Create a guild, or run guildSetupTour.start(guildId), to see it again.");
    },
    /** Current persisted state: 'pending' | 'done' | null. */
    status: () => ({ flag: readFlag(), guildId: readTourGuild() }),
    /** List the steps. */
    steps: () => STEPS.map((s, i) => `${i}: ${s.key} → ${s.title} (${s.stage})`),
    help: () => {
      console.info(
        [
          "%c GuildSetupTour test commands ",
          "",
          "  guildSetupTour.start(guildId)   Force-play from step 1 (guildId optional if one is stored)",
          "  guildSetupTour.goto(n)          Jump to a step (0=join link, 1=Discord bot, 2=release, 3=dashboard switch, 4=recruit)",
          "  guildSetupTour.request(guildId) Simulate a real guild-creation trigger",
          "  guildSetupTour.reset()          Clear the 'seen' flag",
          "  guildSetupTour.status()         { flag, guildId }",
          "  guildSetupTour.steps()          List the steps",
          "",
          "Tip: open a guild you own, grab its id from the URL/state, then run start(id).",
        ].join("\n"),
        "background:#f59e0b;color:#000;font-weight:bold;border-radius:3px"
      );
    },
  };
}
